import React, { useState, useMemo } from 'react';
import { Card, Container, InputGroup, FormControl, ListGroup } from 'react-bootstrap';
import { Radio, Select, Tag } from 'antd';
import { UnorderedListOutlined, BarChartOutlined, SearchOutlined, SyncOutlined } from '@ant-design/icons';
import GanttView from '../ShiftBat/GanttView';
import './Rosters.css';
import useBlocksAsShiftBats from '../../hooks/useBlocksAsShiftBats';
import { useAppContext } from '../../libs/contextLib';
import { debounce } from 'lodash';
import { toHrsMinsSecs, toKmMs, toTime } from '../../libs/formatLib';

// Define filterItems for blocks (simplified for now)
const filterItems = {
  Active: { name: 'Active', color: '#3f8a35', showFn: (r) => !r.expired },
  NoTrips: { name: 'No Trips', color: '#656d88', showFn: (r) => r.noTrips },
  // Add more block-specific tags as needed
};

// Custom filter function using OR logic
const customFilterFn = (items, filterItems, filterBy) => {
  if (!items || !Array.isArray(items)) return items;
  if (!filterBy?.length) return items;
  return items.filter(item => filterBy.some(filter => filterItems[filter]?.showFn?.(item)));
};

const Blocks = () => {
  const { setPageTitle } = useAppContext();
  const [viewMode, setViewMode] = useState('gantt');
  const [sortBy, setSortBy] = useState('code');
  const { shiftBats, isLoading } = useBlocksAsShiftBats();
  const [filterBy, setFilterBy] = useState([]);
  const [filter, setFilter] = useState('');
  const setFilterDebounced = useMemo(() => debounce(setFilter, 250), []);

  // Sorting function
  const sortShiftBats = (shiftBats, sortType) => {
    if (sortType === 'time') {
      return [...shiftBats].sort((a, b) => {
        const aTime = a.getStartTime?.({ asDayJs: true });
        const bTime = b.getStartTime?.({ asDayJs: true });
        if (!aTime || !bTime) return 0;
        return aTime.diff(bTime);
      });
    }
    return [...shiftBats].sort((a, b) => {
      if (!a.shiftBatName?.length || !b.shiftBatName?.length) return 1;
      return a.shiftBatName.localeCompare(b.shiftBatName);
    });
  };

  // Filtering and search logic
  const filteredShiftBats = useMemo(() => {
    let filtered = shiftBats || [];
    if (filter && filter.length) {
      const _filter = filter.toLowerCase();
      filtered = filtered.filter(r =>
        (r.shiftBatName && r.shiftBatName.toLowerCase().includes(_filter))
      );
    }
    filtered = customFilterFn(filtered, filterItems, filterBy);
    return sortShiftBats(filtered, sortBy);
  }, [shiftBats, filter, filterBy, sortBy]);

  // List view for blocks
  function renderBlockList(blocks) {
    return (
      <>
        {isLoading ? <SyncOutlined spin /> :
          <>
            <ListGroup>
              <div className="row list-group-header">
                <div className="col-lg-4">Block Name</div>
                <div className="col-lg-2 text-center">Sign On</div>
                <div className="col-lg-2 text-center">Sign Off</div>
                <div className="col-lg-2 text-center">Duration</div>
                <div className="col-lg-1 text-center">Distance</div>
                <div className="col-lg-1 text-center">Tags</div>
              </div>
              {blocks.map(block => (
                <ListGroup.Item key={block.shiftBatId}>
                  <div className="row">
                    <div className="col-lg-4">{block.shiftBatName}</div>
                    <div className="col-lg-2 text-center">{toTime(block.getStartTime())}</div>
                    <div className="col-lg-2 text-center">{toTime(block.getEndTime())}</div>
                    <div className="col-lg-2 text-center">{toHrsMinsSecs(block.getShiftTime?.(), false, true)}</div>
                    <div className="col-lg-1 text-center">{toKmMs(block.getShiftDistance?.(), 0)}</div>
                    <div className="col-lg-1 d-flex justify-content-around simple-tags">
                      {Object.keys(filterItems).map(key => {
                        const item = filterItems[key];
                        if (item.showFn(block)) {
                          return <Tag key={key} color={item.color}>{item.name || key}</Tag>;
                        }
                        return null;
                      })}
                    </div>
                  </div>
                </ListGroup.Item>
              ))}
            </ListGroup>
          </>
        }
      </>
    );
  }

  return (
    <div className="blocks ShiftBat">
      <div className="d-flex align-items-center justify-content-between filter-options-main">
        <div className="d-flex align-items-center" style={{ gap: '10px' }}>
          <Radio.Group
            value={viewMode}
            onChange={e => setViewMode(e.target.value)}
            buttonStyle="solid"
            className="view-mode-toggle ml-0"
          >
            <Radio.Button value="list">
              <UnorderedListOutlined /> List
            </Radio.Button>
            <Radio.Button value="gantt">
              <BarChartOutlined /> Gantt
            </Radio.Button>
          </Radio.Group>
          <Select
            value={sortBy}
            onChange={setSortBy}
            style={{ width: 150, marginLeft: '10px' }}
            options={[
              { value: 'time', label: 'Sign-on Time' },
              { value: 'code', label: 'Block Name' }
            ]}
            defaultValue="code"
          />
        </div>
        <div className="d-flex align-items-center">
          <div className="justify-content-end w-separator">
            <InputGroup className="search-filter">
              <FormControl
                type="search"
                placeholder="Search"
                className=""
                size="sm"
                aria-label="Filter"
                onChange={e => setFilterDebounced(e.target.value || "")}
              />
              <InputGroup.Text id="basic-addon1"><SearchOutlined /></InputGroup.Text>
            </InputGroup>
          </div>
          <div className="filter-switches">
            <div style={{ display: 'flex', alignItems: 'center' }}>
              <Select
                mode="multiple"
                placeholder="Filter by"
                style={{ width: 300 }}
                value={filterBy}
                onChange={setFilterBy}
                options={Object.keys(filterItems).map(key => {
                  const item = filterItems[key];
                  return {
                    value: key,
                    label: (
                      <div style={{ display: 'flex', alignItems: 'center' }}>
                        <div
                          style={{
                            width: 12,
                            height: 12,
                            borderRadius: '50%',
                            backgroundColor: item.color,
                            marginRight: 8,
                            border: '1px solid #d9d9d9'
                          }}
                        />
                        {item.name || key}
                      </div>
                    ),
                    labelString: item.name || key,
                  };
                })}
                optionLabelProp="labelString"
                optionFilterProp="labelString"
                allowClear
              />
            </div>
          </div>
        </div>
      </div>
      {viewMode === 'list' ? (
        renderBlockList(filteredShiftBats)
      ) : (
        <GanttView
          shiftBats={filteredShiftBats}
          onSearch={setFilter}
          sortBy={sortBy}
          setSortBy={setSortBy}
          ganttTitle="Block"
          showAddDriverDutyButton={false}
        />
      )}
    </div>
  );
};

export default Blocks; 